import { useState } from 'react';
import { api } from '../lib/api';
import MyRequests from '../components/MyRequests';

// "Service Request" form for repair/maintenance on customer equipment.
// Routes to services@silverservice.com (separate inbox from orders).
const URGENCIES = ['', 'ASAP — equipment is down', 'Within a few days', 'Within a week', 'No rush'];
const EMPTY_FORM = {
  equipment: '',
  issue: '',
  urgency: '',
  preferredDate: '',
  contactPhone: '',
  additionalNotes: '',
};

export default function ServiceRequest() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.equipment.trim() || !form.issue.trim() || !form.contactPhone.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await api.sendServiceRequest(form);
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) return (
    <div style={styles.successWrap}>
      <div style={styles.successIcon}>✓</div>
      <h2 style={styles.successTitle}>Request Sent!</h2>
      <p style={styles.successText}>
        Your service request has been sent to the Silver Service team. They'll
        reach out shortly to schedule.
      </p>
      <button
        style={styles.backBtn}
        onClick={() => { setSubmitted(false); setForm(EMPTY_FORM); }}
      >
        Submit another request
      </button>
    </div>
  );

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <h1 style={styles.title}>Service Request</h1>
        <p style={styles.subtitle}>
          Request preventative maintenance, a repair, or a quote for new
          equipment. We'll be in touch to schedule a visit.
        </p>
      </div>

      <div style={styles.card}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Equipment <span style={{color:'#e74c3c'}}>*</span></label>
            <input
              style={styles.input}
              placeholder="e.g. Bunn coffee brewer at the front bar"
              value={form.equipment}
              onChange={e => set('equipment', e.target.value)}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>What's the issue? <span style={{color:'#e74c3c'}}>*</span></label>
            <textarea
              style={{...styles.input, resize:'vertical', minHeight:120}}
              placeholder="Describe the problem in plain language. Anything you've already tried, error codes, when it started, etc."
              value={form.issue}
              onChange={e => set('issue', e.target.value)}
              required
            />
          </div>
          <div style={styles.twoCol}>
            <div style={styles.field}>
              <label style={styles.label}>Urgency</label>
              <select style={styles.input} value={form.urgency} onChange={e => set('urgency', e.target.value)}>
                {URGENCIES.map(u => <option key={u} value={u}>{u || '— Select —'}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Preferred date</label>
              <input style={styles.input} type="date" value={form.preferredDate} onChange={e => set('preferredDate', e.target.value)} />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Contact phone <span style={{color:'#e74c3c'}}>*</span></label>
            <input
              style={styles.input}
              type="tel"
              placeholder="Best number for our tech team to reach you"
              value={form.contactPhone}
              onChange={e => set('contactPhone', e.target.value)}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Additional notes</label>
            <textarea
              style={{...styles.input, resize:'vertical', minHeight:80}}
              placeholder="Best access times, parking instructions, who to ask for, etc."
              value={form.additionalNotes}
              onChange={e => set('additionalNotes', e.target.value)}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button
            style={styles.btn}
            type="submit"
            disabled={submitting || !form.equipment.trim() || !form.issue.trim() || !form.contactPhone.trim()}
          >
            {submitting ? 'Sending…' : 'Send service request'}
          </button>
        </form>
      </div>
      <MyRequests type="service" />
    </div>
  );
}

const styles = {
  root: { maxWidth: 600, margin: '0 auto' },
  header: { marginBottom:'1.5rem' },
  title: { fontSize:'1.5rem', fontWeight:700, color:'#333', margin:0 },
  subtitle: { fontSize:'0.85rem', color:'rgba(0,0,0,0.55)', margin:'0.4rem 0 0', lineHeight:1.5 },
  card: { background:'#fff', border:'1px solid #e0e0e0', borderRadius:10, padding:'2rem', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' },
  form: { display:'flex', flexDirection:'column', gap:'1.25rem' },
  twoCol: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' },
  field: { display:'flex', flexDirection:'column', gap:'0.4rem' },
  label: { fontSize:'0.78rem', color:'rgba(0,0,0,0.5)', fontWeight:500, letterSpacing:'0.04em' },
  input: { background:'#fff', border:'1px solid #ddd', borderRadius:6, padding:'0.7rem 0.9rem', color:'#333', fontSize:'0.9rem', outline:'none', fontFamily:'inherit' },
  error: { background:'rgba(231,76,60,0.08)', border:'1px solid rgba(231,76,60,0.25)', borderRadius:6, padding:'0.75rem 1rem', color:'#c0392b', fontSize:'0.85rem' },
  btn: { background:'#A50B18', color:'#fff', border:'none', borderRadius:6, padding:'0.85rem', fontSize:'0.95rem', fontWeight:700, cursor:'pointer' },
  successWrap: { maxWidth:400, margin:'4rem auto', textAlign:'center' },
  successIcon: { width:64, height:64, background:'rgba(39,174,96,0.1)', border:'2px solid #27ae60', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1.5rem', fontSize:'1.8rem', color:'#27ae60' },
  successTitle: { fontSize:'1.4rem', color:'#333', margin:'0 0 0.5rem' },
  successText: { color:'rgba(0,0,0,0.5)', lineHeight:1.6, margin:'0 0 2rem' },
  backBtn: { background:'none', border:'1px solid #ddd', color:'rgba(0,0,0,0.5)', borderRadius:6, padding:'0.7rem 1.5rem', cursor:'pointer', fontSize:'0.9rem' },
};
